<template>
    <modal id="place-user-form" ref="userForm" @close="closeUserModal">
        <template slot="title">{{ t('places-user_form-title') }}</template>
        <template slot="body">
            <form id="user-full-form" method="post" @submit.prevent>
                <div class="field">
                    <label>{{ t('places-user_form-name') }}</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="userForm.name"
                        required
                        @input="clearCustomValidity($event); validateName()"
                        :title="t('places-user_form-name_title')"
                        @invalid.prevent="setCustomValidity($event, 'places-user_form-validation-name_required')"
                        minlength="3"
                    />
                    <span v-if="errors.name" class="error">
                        {{ errors.name }}
                    </span>
                </div>
                <div class="field">
                    <label>{{ t('places-user_form-email') }}</label>
                    <input
                        type="email"
                        class="form-control"
                        v-model="userForm.email"
                        required
                        @input="validateEmail()"
                        :title="t('places-user_form-email_title')"
                    />
                    <span v-if="errors.email" class="error">
                        <template v-if="errors.email === 'taken'">{{ t('places-user_form-validation-email_taken') }}</template>
                        <template v-else>{{ errors.email }}</template>
                    </span>
                </div>
                <div class="field" v-if="$module('whatsapp')">
                    <label>{{ t('places-user_form-whatsapp') }}</label>
                    <select
                        v-model="userForm.whatsapp_places_senders_id"
                        class="form-control"
                    >
                        <option :value="null">
                            {{ t('places-user_form-no_whatsapp_instance') }}
                        </option>
                        <option
                            v-for="sender in whatsappSenders"
                            :value="sender.id"
                            :key="sender.id"
                        >
                            {{ sender.alias }}
                        </option>
                    </select>
                </div>
                <div class="field">
                    <label>{{ t('places-user_form-global_admin') }}</label>
                    <select
                        v-model="userForm.global_admin"
                        class="form-control"
                        required
                        :title="t('places-user_form-global_admin_title')"
                        @input="clearCustomValidity($event)"
                        @invalid.prevent="setCustomValidity($event, 'places-user_form-validation-permissions_required')"
                    >
                        <option value="0">{{ t('places-user_form-no') }}</option>
                        <option value="1">{{ t('places-user_form-yes') }}</option>
                    </select>
                    <div v-if="userForm.global_admin === '1'" class="field alert alert-danger h-auto mt-2">
                        {{ t('places-user_form-global_admin_warning') }}
                    </div>
                    <span v-if="errors.permissions" class="error">
                        {{ errors.permissions }}
                    </span>
                </div>
                <div class="field">
                    <label>{{ t('places-user_form-is_internal') }}</label>
                    <select
                        v-model="userForm.is_internal"
                        class="form-control"
                        required
                        :title="t('places-user_form-is_internal_title')"
                        @input="clearCustomValidity($event)"
                        @invalid.prevent="setCustomValidity($event, 'places-user_form-validation-is_internal_required')"
                    >
                        <option value="0">{{ t('places-user_form-no') }}</option>
                        <option value="1">{{ t('places-user_form-yes') }}</option>
                    </select>
                </div>
                <div class="field" v-if="activeUser">
                    <label>{{ t('places-user_form-active') }}</label>
                    <select
                        v-model="userForm.active"
                        class="form-control"
                        required
                        @change="clearCustomValidity($event); validateActive()"
                        :title="t('places-user_form-active_title')"
                        @invalid.prevent="setCustomValidity($event, 'places-user_form-validation-active_required')"
                    >
                        <option value="0">{{ t('places-user_form-no') }}</option>
                        <option value="1">{{ t('places-user_form-yes') }}</option>
                    </select>
                    <span v-if="errors.active" class="error">
                        {{ errors.active }}
                    </span>
                </div>
                <div class="field" v-if="branches.length > 0">
                    <label>{{ t('places-user_form-branch') }}</label>
                    <select
                        v-model="userForm.places_branches_id"
                        class="form-control"
                        required
                        @change="clearCustomValidity($event); validatePlacesBranches()"
                        :title="t('places-user_form-branch_title')"
                        @invalid.prevent="setCustomValidity($event, 'places-user_form-validation-branch_required')"
                    >
                        <option
                            v-for="branch in branches"
                            :value="branch.id"
                            :key="branch.id"
                        >
                            {{ branch.title }}
                        </option>
                    </select>
                    <span v-if="errors.placesBranches" class="error">
                        {{ errors.placesBranches }}
                    </span>
                </div>
                <div
                    class="field"
                    v-if="roles.length > 0"
                >
                    <label>{{ t('places-user_form-roles') }}</label>
                    <div v-for="role in roles" :key="role.id">
                        <label>
                            <input
                                v-model="userForm.roles"
                                type="checkbox"
                                :value="role.id"
                            />
                            <!-- i18n-validator-ignore-next-line: runtime role title -->
                            {{ role.title }}
                        </label>
                    </div>
                </div>
                <div class="field">
                    <label>{{ t('places-user_form-username') }}</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="userForm.username"
                        required
                        @input="clearCustomValidity($event); validateUsername()"
                        :title="t('places-user_form-username_title')"
                        @invalid.prevent="setCustomValidity($event, 'places-user_form-validation-username_required')"
                        minlength="3"
                    />
                    <span v-if="errors.username" class="error">
                        <template v-if="errors.username === 'taken'">{{ t('places-user_form-validation-username_taken') }}</template>
                        <template v-else>{{ errors.username }}</template>
                    </span>
                </div>
                <div class="field">
                    <label>{{ t('places-user_form-password') }}</label>
                    <input
                        type="text"
                        class="form-control"
                        v-model="userForm.password"
                        :title="t('places-user_form-password_title')"
                        @input="clearCustomValidity($event); validatePassword()"
                        @invalid.prevent="setCustomValidity($event, 'places-user_form-validation-password_required')"
                        minlength="6"
                    />
                    <span v-if="errors.password" class="error">
                        <template v-if="errors.password === 'empty'">{{ t('places-user_form-validation-password_required') }}</template>
                        <template v-else>{{ errors.password }}</template>
                    </span>
                </div>
                <div class="field">
                    <label>{{ t('places-user_form-kevi_marketing_enabled') }}</label>
                    <select v-model="userForm.kevi_marketing_enabled" class="form-control">
                        <option value="0">{{ t('places-user_form-no') }}</option>
                        <option value="1">{{ t('places-user_form-yes') }}</option>
                    </select>
                </div>
                <div class="field">
                    <label>{{ t('places-user_form-kevi_chat_enabled') }}</label>
                    <select v-model="userForm.kevi_chat_enabled" class="form-control">
                        <option value="0">{{ t('places-user_form-no') }}</option>
                        <option value="1">{{ t('places-user_form-yes') }}</option>
                    </select>
                </div>
                <div class="field" v-if="activeUser">
                    <label>{{ t('places-user_form-kevi_presence') }}</label>
                    <p class="text-sm text-gray-600 mb-0">{{ presenceLabel }}</p>
                    <p
                        v-if="chatPresenceHint"
                        class="text-xs text-gray-500 mt-1 mb-0"
                    >
                        {{ chatPresenceHint }}
                    </p>
                </div>
                <div class="field">
                    <h3>{{ t('places-user_form-permissions') }}</h3>
                    <div>
                        <div>
                            <label for="permissions-dashboard">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.dashboard.enabled
                                    "
                                    value="1"
                                    id="permissions-dashboard"
                                />
                                <span>{{ t('places-user_form-permission-dashboard') }}</span>
                            </label>
                            <label
                                class="permission-options"
                                v-if="userForm.permissions.dashboard.enabled"
                            >
                                <span
                                    >{{ t('places-user_form-permission-access_operations') }}</span
                                >
                                <select
                                    class="form-control"
                                    v-model="
                                        userForm.permissions.dashboard.options.level
                                    "
                                    required
                                    @select="validateLevelAccess('dashboard', 'dashboardAccess', dashboardErrorMessage)"
                                    :title="t('places-user_form-permission-dashboard_access_title')"
                                    @input="clearCustomValidity($event)"
                                    @invalid.prevent="setCustomValidityMessage($event, dashboardErrorMessage)"
                                >
                                    <option value="insert">{{ t('places-user_form-permission-level_insert') }}</option>
                                    <option value="read">{{ t('places-user_form-permission-level_read') }}</option>
                                </select>
                                <span v-if="errors.dashboardAccess" class="error">
                                    {{ errors.dashboardAccess }}
                                </span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-inbox">
                                <input
                                    type="checkbox"
                                    v-model="userForm.permissions.inbox.enabled"
                                    value="1"
                                    id="permissions-inbox"
                                />
                                <span>{{ t('places-user_form-permission-inbox') }}</span>
                            </label>
                            <label
                                v-if="userForm.permissions.inbox.enabled"
                                class="permission-options"
                                for="permissions-inbox--global"
                            >
                                <input
                                    :disabled="noHasPlacesBranches"
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.inbox.options
                                            .global_access
                                    "
                                    value="1"
                                    id="permissions-inbox--global"
                                    true-value="1"
                                    false-value=""
                                />
                                <span
                                    >{{ t('places-user_form-permission-global_access') }}</span
                                >
                            </label>
                        </div>
                        <div>
                            <label for="permissions-tasks">
                                <input
                                    type="checkbox"
                                    v-model="userForm.permissions.tasks.enabled"
                                    value="1"
                                    id="permissions-tasks"
                                />
                                <span>{{ t('places-user_form-permission-tasks') }}</span>
                            </label>
                            <label
                                class="permission-options"
                                v-if="userForm.permissions.tasks.enabled"
                            >
                                <span
                                    >{{ t('places-user_form-permission-access_tasks') }}</span
                                >
                                <select
                                    class="form-control"
                                    v-model="
                                        userForm.permissions.tasks.options.level
                                    "
                                    required
                                    @select="validateLevelAccess('tasks', 'tasksAccess', taskErrorMessage)"
                                    :title="t('places-user_form-permission-tasks_access_title')"
                                    @input="clearCustomValidity($event)"
                                    @invalid.prevent="setCustomValidityMessage($event, taskErrorMessage)"
                                >
                                    <option value="mine">{{ t('places-user_form-permission-scope_mine') }}</option>
                                    <option value="branch">{{ t('places-user_form-permission-scope_branch') }}</option>
                                    <option value="all">{{ t('places-user_form-permission-scope_all_feminine') }}</option>
                                </select>
                                <span v-if="errors.tasksAccess" class="error">
                                    {{ errors.tasksAccess }}
                                </span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-coupons">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.coupons.enabled
                                    "
                                    value="1"
                                    id="permissions-coupons"
                                />
                                <span>{{ t('places-user_form-permission-coupons') }}</span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-clients">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.clients.enabled
                                    "
                                    value="1"
                                    id="permissions-clients"
                                />
                                <span>{{ t('places-user_form-permission-clients') }}</span>
                            </label>
                            <label
                                v-if="userForm.permissions.clients.enabled"
                                class="permission-options"
                                for="permissions-clients--global"
                            >
                                <input
                                    :disabled="noHasPlacesBranches"
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.clients.options
                                            .global_access
                                    "
                                    value="1"
                                    id="permissions-clients--global"
                                    true-value="1"
                                    false-value=""
                                />
                                <span
                                    >{{ t('places-user_form-permission-global_access') }}</span
                                >
                            </label>
                            <label
                                v-if="userForm.permissions.clients.enabled"
                                class="permission-options"
                                for="permissions-clients--deletion"
                            >
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.clients.options
                                            .deletion
                                    "
                                    value="1"
                                    id="permissions-clients--deletion"
                                    true-value="1"
                                    false-value=""
                                />
                                <span
                                    >{{ t('places-user_form-permission-delete_clients') }}</span
                                >
                            </label>
                        </div>
                        <div>
                            <label for="permissions-segments">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.segments.enabled
                                    "
                                    value="1"
                                    id="permissions-segments"
                                />
                                <span>{{ t('places-user_form-permission-segments') }}</span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-rewards">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.rewards.enabled
                                    "
                                    value="1"
                                    id="permissions-rewards"
                                />
                                <span>{{ t('places-user_form-permission-rewards') }}</span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-reports">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.reports.enabled
                                    "
                                    value="1"
                                    id="permissions-reports"
                                />
                                <span>{{ t('places-user_form-permission-reports') }}</span>
                            </label>
                            <label
                                v-if="userForm.permissions.reports.enabled"
                                class="permission-options"
                                for="permissions-reports--global"
                            >
                                <input
                                    :disabled="noHasPlacesBranches"
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.reports.options
                                            .global_access
                                    "
                                    value="1"
                                    id="permissions-reports--global"
                                    true-value="1"
                                    false-value=""
                                />
                                <span
                                    >{{ t('places-user_form-permission-global_access') }}</span
                                >
                            </label>
                        </div>
                        <div>
                            <label for="permissions-forms">
                                <input
                                    type="checkbox"
                                    v-model="userForm.permissions.forms.enabled"
                                    value="1"
                                    id="permissions-forms"
                                />
                                <span>{{ t('places-user_form-permission-forms') }}</span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-automations">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.automations.enabled
                                    "
                                    value="1"
                                    id="permissions-automations"
                                />
                                <span>{{ t('places-user_form-permission-automations') }}</span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-balance">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.balance.enabled
                                    "
                                    value="1"
                                    id="permissions-balance"
                                />
                                <span>{{ t('places-user_form-permission-balance') }}</span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-configs">
                                <input
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.configs.enabled
                                    "
                                    value="1"
                                    id="permissions-configs"
                                />
                                <span>{{ t('places-user_form-permission-configs') }}</span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-quotations">
                                <input
                                    type="checkbox"
                                    v-model="userForm.permissions.quotations.enabled"
                                    value="1"
                                    id="permissions-quotations"
                                />
                                <span>{{ t('places-user_form-permission-quotations') }}</span>
                            </label>
                            <label
                                class="permission-options"
                                v-if="userForm.permissions.quotations.enabled"
                            >
                                <span
                                    >{{ t('places-user_form-permission-access_quotations') }}</span
                                >
                                <select
                                    class="form-control"
                                    v-model="
                                        userForm.permissions.quotations.options.level
                                    "
                                    required
                                    @select="validateLevelAccess('quotations', 'quotationsAccess', quotationsErrorMessage)"
                                    :title="t('places-user_form-permission-quotations_access_title')"
                                    @input="clearCustomValidity($event)"
                                    @invalid.prevent="setCustomValidityMessage($event, quotationsErrorMessage)"
                                >
                                    <option value="mine">{{ t('places-user_form-permission-scope_mine_plural') }}</option>
                                    <option value="branch">{{ t('places-user_form-permission-scope_branch') }}</option>
                                    <option value="all">{{ t('places-user_form-permission-scope_all_plural') }}</option>
                                </select>
                                <span v-if="errors.quotationsAccess" class="error">
                                    {{ errors.quotationsAccess }}
                                </span>
                            </label>
                        </div>
                        <div>
                            <label for="permissions-mcp">
                                <input
                                    type="checkbox"
                                    v-model="userForm.permissions.mcp.enabled"
                                    value="1"
                                    id="permissions-mcp"
                                />
                                <span>{{ t('places-user_form-permission-mcp') }}</span>
                            </label>
                        </div>

                        <div>
                            <label for="permissions-invoices">
                                <input
                                    type="checkbox"
                                    v-model="userForm.permissions.invoices.enabled"
                                    value="1"
                                    id="permissions-invoices"
                                />
                                <span>{{ t('places-user_form-permission-invoices') }}</span>
                            </label>
                            <label
                                v-if="userForm.permissions.invoices.enabled"
                                class="permission-options"
                                for="permissions-invoices--global"
                            >
                                <input
                                    :disabled="noHasPlacesBranches"
                                    type="checkbox"
                                    v-model="
                                        userForm.permissions.invoices.options
                                            .global_access
                                    "
                                    value="1"
                                    id="permissions-invoices--global"
                                    true-value="1"
                                    false-value=""
                                />
                                <span
                                    >{{ t('places-user_form-permission-global_access') }}</span
                                >
                            </label>
                        </div>
                        <div>
                            <label for="permissions-benefit_programs">
                                <input
                                    type="checkbox"
                                    v-model="userForm.permissions.benefit_programs.enabled"
                                    value="1"
                                    id="permissions-benefit_programs"
                                />
                                <span>{{ t('places-user_form-permission-benefit_programs') }}</span>
                            </label>
                        </div>
                    </div>
                </div>
                <input
                    type="submit"
                    ref="userSubmit"
                    style="visibility: hidden"
                />
            </form>
        </template>
        <template slot="footer">
            <button type="button" class="btn btn-secondary" @click="close()">
                {{ t('places-user_form-cancel') }}
            </button>
            <button
                type="button"
                class="btn btn-primary"
                @click="saveUser"
                :disabled="savingUser"
            >
                {{ t('places-user_form-confirm') }}
            </button>
        </template>
    </modal>
</template>
<style lang="less" scoped>
.error {
    color: red;
    font-size: 0.9em;
}
div.field {
    margin-bottom: 12px;
}
</style>
<script>
import { mapState, mapActions, mapMutations } from "vuex";
import keviApi from "../../../keviApi";
export default {
    data() {
        return {
            savingUser: false,
            userForm: {
                id: null,
                active: 1,
                global_admin: "0",
                is_internal: "0",
                places_branches_id: null,
                name: null,
                email: null,
                username: null,
                password: null,
                roles: [],
                whatsapp_places_senders_id: null,
                kevi_chat_enabled: "0",
                kevi_marketing_enabled: "1",
                permissions: {
                    dashboard: {
                        enabled: 0,
                        options: {
                            level: "",
                        },
                    },
                    tasks: {
                        enabled: 0,
                        options: {
                            level: "",
                        },
                    },
                    coupons: {
                        enabled: 0,
                        options: {},
                    },
                    clients: {
                        enabled: 0,
                        options: {
                            global_access: 0,
                            deletion: 0
                        },
                    },
                    rewards: {
                        enabled: 0,
                        options: {},
                    },
                    reports: {
                        enabled: 0,
                        options: {
                            global_access: 0,
                        },
                    },
                    forms: {
                        enabled: 0,
                        options: {},
                    },
                    automations: {
                        enabled: 0,
                        options: {},
                    },
                    balance: {
                        enabled: 0,
                        options: {},
                    },
                    inbox: {
                        enabled: 0,
                        options: {
                            global_access: 0,
                        },
                    },
                    configs: {
                        enabled: 0,
                        options: {},
                    },
                    segments: {
                        enabled: 0,
                        options: {},
                    },
                    quotations:  {
                        enabled: 0,
                        options: {
                            level: "",
                        },
                    },
                    mcp: {
                        enabled: 0,
                        options: {},
                    },
                    invoices: {
                        enabled: 0,
                        options: {
                            global_access: 0,
                        },
                    },
                    benefit_programs: {
                        enabled: 0,
                        options: {},
                    },
                },
            },
            errors: {
                name: "",
                email: "",
                password: "",
                permissions: "",
                active: "",
                placesBranches: "",
                username: "",
                tasksAccess: "",
                dashboardAccess: "",
                quotationsAccess: "",
            },
        };
    },
    computed: {
        ...mapState({
            users: (state) => state.placeDetails.users,
            branches: (state) => state.placeDetails.branches,
            roles: (state) => state.placeDetails.roles,
            activeUser: (state) => state.placeDetails.activeUser,
            modalVisible: (state) => state.placeDetails.modalVisible,
            placeId: (state) => state.placeDetails.id,
            senders: (state) => state.placeDetails.senders,
        }),
        whatsappSenders() {
            return this.senders.filter((sender) => sender.channel === "whatsapp");
        },
        presenceKey() {
            const chatEnabled = Number(this.userForm.kevi_chat_enabled) === 1;
            const mktEnabled = Number(this.userForm.kevi_marketing_enabled) === 1;

            if (mktEnabled && chatEnabled) {
                return "both";
            }
            if (chatEnabled) {
                return "chat";
            }
            if (mktEnabled) {
                return "marketing";
            }
            return "none";
        },
        presenceLabel() {
            return this.t("places-user_form-presence_" + this.presenceKey);
        },
        chatPresenceHint() {
            if (this.presenceKey !== "marketing") {
                return "";
            }
            return this.t(
                "places-user_form-kevi_presence_hint_marketing_only",
            );
        },
        noHasPlacesBranches() {
            return !this.userForm.places_branches_id;
        },
        taskErrorMessage() {
            return this.t("places-user_form-validation-tasks_access_required");
        },
        quotationsErrorMessage() {
            return this.t("places-user_form-validation-quotations_access_required");
        },
        dashboardErrorMessage() {
            return this.t("places-user_form-validation-dashboard_access_required");
        },
    },
    methods: {
        flagValue(value, fallbackOn) {
            if (value === true || value === 1 || value === "1") return "1";
            if (value === false || value === 0 || value === "0") return "0";
            return fallbackOn ? "1" : "0";
        },
        clearCustomValidity(event) {
            event.target.setCustomValidity("");
        },
        setCustomValidity(event, key) {
            event.target.setCustomValidity(this.t(key));
        },
        setCustomValidityMessage(event, message) {
            event.target.setCustomValidity(message);
        },
        closeUserModal() {
            if (this.modalVisible === "user-form") {
                this.close();
            }
        },
        validateName() {
            if (!this.userForm.name) {
                this.errors.name = this.t("places-user_form-validation-name_required");
            } else if (this.userForm.name.length < 3) {
                this.errors.name = this.t("places-user_form-validation-name_min_length");
            } else {
                this.errors.name = null;
            }
        },
        validateEmail() {
            // ASCII-only (sem IDN), domínio com labels válidos, TLD 2–63
            const emailPattern =
              /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
            if (!this.userForm.email) {
                this.errors.email = this.t("places-user_form-validation-email_required");
            } else if (!emailPattern.test(this.userForm.email)) {
                this.errors.email = this.t("places-user_form-validation-email_valid");
            } else {
                this.errors.email = null;
            }
        },
        validatePassword() {
            if (this.userForm.password && this.userForm.password.length < 6) {
                this.errors.password = this.t(
                    "places-user_form-validation-password_min_length",
                );
            } else {
                this.errors.password = null;
            }
        },
        validatePermissions() {
            if (this.userForm.global_admin === "") {
                this.errors.permissions = this.t(
                    "places-user_form-validation-permissions_required",
                );
            } else {
                this.errors.permissions = null;
            }
        },
        validateActive() {
            if (this.userForm.active === "") {
                this.errors.active = this.t("places-user_form-validation-active_required");
            } else {
                this.errors.active = null;
            }
        },
        validatePlacesBranches() {
            if (!this.userForm.places_branches_id && this.branches.length > 0) {
                this.errors.placesBranches = this.t(
                    "places-user_form-validation-branch_required",
                );
            } else {
                this.errors.placesBranches = null;
            }
        },
        validateUsername() {
            if (!this.userForm.username) {
                this.errors.username = this.t(
                    "places-user_form-validation-username_required",
                );
            } else if (this.userForm.username.length < 3) {
                this.errors.username = this.t(
                    "places-user_form-validation-username_min_length",
                );
            } else {
                this.errors.username = null;
            }
        },
        validateLevelAccess(permissionKey, errorKey, message) {
            if (this.userForm.permissions[permissionKey].enabled && !this.userForm.permissions[permissionKey].options.level) {
                this.errors[errorKey] = message;
            } else {
                this.errors[errorKey] = null;
            }
        },

        ...mapMutations({
            close: "placeDetails/closeModal",
        }),
        ...mapActions({
            loadUsers: "placeDetails/loadUsers",
        }),
        validateAll() {
            this.validateName();
            this.validateEmail();
            this.validatePassword();
            this.validatePermissions();
            this.validateActive();
            this.validatePlacesBranches();
            this.validateUsername();
            this.validateLevelAccess("tasks", "tasksAccess", this.taskErrorMessage);
            this.validateLevelAccess("dashboard", "dashboardAccess", this.dashboardErrorMessage);
            this.validateLevelAccess("quotations", "quotationsAccess", this.quotationsErrorMessage);

            return !this.hasErrors();
        },
        hasErrors() {
            return (
                Object.values(this.errors).some(error => error !== null)
            );
        },
        saveUser: function () {
            if (!this.validateAll()) {
                console.log(this.errors);
                this.$store.dispatch("notification/warning", {
                    text: this.t("places-user_form-notification-fill_fields"),
                    duration: 5000,
                });
                return;
            }
            let form = document.getElementById("user-full-form");
            this.$refs.userSubmit.click();
            if (form.checkValidity()) {
                this.savingUser = true;
                let userData = {
                    ...this.userForm,
                    places_id: this.placeId,
                    kevi_marketing_enabled: this.flagValue(this.userForm.kevi_marketing_enabled, true) === "1" ? 1 : 0,
                    kevi_chat_enabled: this.flagValue(this.userForm.kevi_chat_enabled, false) === "1" ? 1 : 0,
                };
                let method = this.activeUser ? "POST" : "PUT";
                let endpoint = this.activeUser
                    ? "users/" + userData.id
                    : "users";
                let options = keviApi.getOptions(endpoint, method, userData);
                this.$http(options).then(
                    (_response) => {
                        this.savingUser = false;
                        this.loadUsers();
                        this.close();
                        this.resetUserForm();
                        this.$store.dispatch("notification/success", {
                            text: this.t("places-user_form-notification-saved"),
                            duration: 5000,
                        });
                    },
                    (response) => {
                        const payload = (response && (response.body || response.data)) || {};
                        const fieldErrors = payload.errors || {};
                        for (const key in fieldErrors) {
                            this.errors[key] = fieldErrors[key];
                        }
                        this.$store.dispatch("notification/warning", {
                            text: payload.message
                                ? String(payload.message)
                                : this.t("places-user_form-notification-fill_fields"),
                            duration: 5000,
                        });
                        this.savingUser = false;
                    },
                );
            }
        },
        resetUserForm: function () {
            let permissions = {
                dashboard: {
                    enabled: 0,
                    options: {
                        level: "",
                    },
                },
                tasks: {
                    enabled: 0,
                    options: {
                        level: "",
                    },
                },
                coupons: {
                    enabled: 0,
                    options: {},
                },
                clients: {
                    enabled: 0,
                    options: {
                        global_access: 0,
                        deletion: 0,
                    },
                },
                rewards: {
                    enabled: 0,
                    options: {},
                },
                reports: {
                    enabled: 0,
                    options: {
                        global_access: 0,
                    },
                },
                forms: {
                    enabled: 0,
                    options: {},
                },
                automations: {
                    enabled: 0,
                    options: {},
                },
                balance: {
                    enabled: 0,
                    options: {},
                },
                inbox: {
                    enabled: 0,
                    options: {
                        global_access: 0,
                    },
                },
                configs: {
                    enabled: 0,
                    options: {},
                },
                segments: {
                    enabled: 0,
                    options: {},
                },
                quotations: {
                    enabled: 0,
                    options: {
                        level: "",
                    },
                },
                mcp: {
                    enabled: 0,
                    options: {},
                },
                invoices: {
                    enabled: 0,
                    options: {
                        global_access: 0,
                    },
                },
                benefit_programs: {
                    enabled: 0,
                    options: {},
                },
            };

            this.errors = {
                name: "",
                email: "",
                password: "",
                permissions: "",
                active: "",
                placesBranches: "",
                username: "",
                tasksAccess: "",
                dashboardAccess: "",
                quotationsAccess: "",
            };

            if (this.activeUser) {
                let activeUser =
                    this.$store.getters["placeDetails/getActiveUser"];
                this.userForm = {
                    id: activeUser.id,
                    active: activeUser.active,
                    places_branches_id: activeUser.places_branches_id,
                    name: activeUser.name,
                    email: activeUser.email,
                    username: activeUser.username,
                    roles: activeUser.roles,
                    password: null,
                    whatsapp_places_senders_id: activeUser.whatsapp_places_senders_id,
                    permissions: { ...permissions, ...activeUser.permissions },
                    global_admin: activeUser.global_admin,
                    is_internal: activeUser.is_internal,
                    kevi_chat_enabled: this.flagValue(activeUser.kevi_chat_enabled, false),
                    kevi_marketing_enabled: this.flagValue(activeUser.kevi_marketing_enabled, true),
                };
            } else {
                this.userForm = {
                    id: null,
                    active: 1,
                    places_branches_id: null,
                    name: null,
                    email: null,
                    username: null,
                    password: null,
                    global_admin: null,
                    is_internal: null,
                    whatsapp_places_senders_id: null,
                    roles: [],
                    permissions,
                    kevi_chat_enabled: "0",
                    kevi_marketing_enabled: "1",
                };
            }


        },
    },
    watch: {
        modalVisible() {
            if (this.modalVisible === "user-form") {
                this.resetUserForm();
                this.$refs.userForm.open();
            } else {
                this.$refs.userForm.close();
            }
        },
        activeUser() {
            this.resetUserForm();
        },
    },
};
</script>
